import type React from 'react';
import { useMemo, useCallback } from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { useFlexLayout, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import FormattedCurrencyContainer from '../../../../components/formatted-currency/container';
import { emptyArray } from 'ui-components/default-props/empty';
import commonMessages from '../common-messages';

import style from '../common-styles.module.less';
import analyticsStyle from '../../analytics-style.module.less';

const TITLE = 'title';
const NAME = 'name';
const QTY = 'qty';
const REVENUE = 'revenue';

const quantityMessages = defineMessages({
  [TITLE]: {
    defaultMessage: 'Sales by Category Quantity',
    id: '3467IR',
    description: 'sales by category quantity table header',
  },
  [NAME]: {
    defaultMessage: 'Name',
    id: 'kEQ33Y',
    description: 'sales by category table name header',
  },
  [QTY]: {
    defaultMessage: 'Qty',
    id: 'Yw6+fG',
    description: 'sales by category table quantity (abbreviated) header',
  },
  [REVENUE]: {
    defaultMessage: 'Revenue',
    id: 'hIVLAq',
    description: 'sales by category table share header',
  },
});

const revenueMessages = defineMessages({
  [TITLE]: {
    defaultMessage: 'Sales by Category Revenue',
    id: 'VWNEek',
    description: 'sales by category quantity table header',
  },
  [NAME]: {
    defaultMessage: 'Name',
    id: 'kEQ33Y',
    description: 'sales by category table name header',
  },
  [REVENUE]: {
    defaultMessage: 'Revenue',
    id: 'hIVLAq',
    description: 'sales by category table share header',
  },
  [QTY]: {
    defaultMessage: 'Qty',
    id: 'Yw6+fG',
    description: 'sales by category table quantity (abbreviated) header',
  },
});

type CategoryRow = {
  name: string;
  qty: string;
  revenue: React.ReactNode;
};

type CategoryData = {
  name: string;
  count: number;
  amount: number;
};

type SalesByCategoryQuantityTableProps = {
  data?: CategoryData[];
  byRevenue?: boolean;
};

export const SalesByCategoryQuantityTable = ({
  data = emptyArray as CategoryData[],
  byRevenue = false,
}: SalesByCategoryQuantityTableProps) => {
  const intl = useIntl();
  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const msgs = byRevenue ? revenueMessages : quantityMessages;

  const tableTitle = useMemo(() => intl.formatMessage(msgs[TITLE]), [intl, msgs]);

  const emptyRow = useMemo(() => (!data.length ? <FormattedMessage {...commonMessages.nullStateMessage} /> : null), [data.length]);

  const formatCell = useCallback(
    (content: React.ReactNode, isAligned: boolean) => {
      if (isShipStationAnalyticsFeatureEnabled && !byRevenue) {
        const alignment = isAligned ? style.right : analyticsStyle.left;
        return <div className={classnames(analyticsStyle['analytics-table-header'], alignment)}>{content}</div>;
      }

      if (isAligned) {
        return <div className={style.right}>{content}</div>;
      }

      return content;
    },
    [isShipStationAnalyticsFeatureEnabled, byRevenue],
  );

  const columns = useMemo(() => {
    const columnOrder: Array<typeof NAME | typeof QTY | typeof REVENUE> =
      isShipStationAnalyticsFeatureEnabled && byRevenue ? [NAME, REVENUE, QTY] : [NAME, QTY, REVENUE];

    const columnDefs = {
      [NAME]: {
        Header: () => formatCell(intl.formatMessage(msgs[NAME]), false),
        accessor: NAME as typeof NAME,
        Cell: ({ value }: { value: React.ReactNode }) => formatCell(value, false),
      },
      [QTY]: {
        Header: () => formatCell(intl.formatMessage(msgs[QTY]), true),
        accessor: QTY as typeof QTY,
        Cell: ({ value }: { value: React.ReactNode }) => formatCell(value, true),
      },
      [REVENUE]: {
        Header: () => formatCell(intl.formatMessage(msgs[REVENUE]), true),
        accessor: REVENUE as typeof REVENUE,
        Cell: ({ value }: { value: React.ReactNode }) => formatCell(value, true),
      },
    };

    return columnOrder.map((key) => columnDefs[key]);
  }, [intl, formatCell, msgs, isShipStationAnalyticsFeatureEnabled, byRevenue]);

  const tableData = useMemo(() => {
    return data.map((category) => ({
      name: category.name,
      qty: intl.formatNumber(category.count),
      revenue: <FormattedCurrencyContainer value={category.amount} />,
    }));
  }, [data, intl]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<CategoryRow>(
    {
      columns,
      data: tableData,
    },
    useFlexLayout,
  );

  return (
    <>
      <div
        className={classnames({
          [style['table-title']]: !isShipStationAnalyticsFeatureEnabled,
          [analyticsStyle['analytics-table-title']]: isShipStationAnalyticsFeatureEnabled,
        })}
      >
        <Text textType={TextType.H3} value={tableTitle} />
      </div>
      <ReactTableWrapper
        className={classnames({
          [analyticsStyle['analytics-table']]: isShipStationAnalyticsFeatureEnabled,
        })}
        emptyRow={emptyRow}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    </>
  );
};
